/**
 * One duel game's lobby (ADR 0076): your games, challenges for you and for anyone, a new
 * challenge, recent results, and the ratings at each pace.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDuel } from "@/duels/registry";
import { useTenant } from "@/core/tenant/TenantProvider";
import { useTopic } from "@/core/realtime/RealtimeProvider";
import { Button } from "@/core/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  LOBBY_TOPIC,
  opponentOf,
  useDuelAction,
  useDuelHub,
  useRatings,
  type DuelSummary,
  type Pace,
} from "./api";
import { ChallengeForm } from "./ChallengeForm";
import { outcomeFor, useReason, useTimeControlLabel } from "./format";

export function DuelLobbyPage() {
  const { key = "" } = useParams();
  const { t } = useTranslation();
  const tenant = useTenant();
  const module = tenant.enabled_games.includes(key) ? getDuel(key) : undefined;
  if (tenant.enabled_games.length === 0)
    return <p className="bt-page-message">{t("games.disabled")}</p>;
  if (!module) return <p className="bt-page-message">{t("games.unknown")}</p>;
  return <Lobby gameKey={key} ns={module.namespace} />;
}

function Lobby({ gameKey, ns }: { gameKey: string; ns: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const hub = useDuelHub(gameKey);
  useTopic(LOBBY_TOPIC, (frame) => {
    if (frame.type === "duels.changed")
      void queryClient.invalidateQueries({ queryKey: ["duels", "hub"] });
  });
  if (hub.isPending) return <p className="bt-page-message">{t("app.loading")}</p>;
  if (hub.isError || !hub.data)
    return <p className="bt-page-message bt-page-message--error">{t("duels.load_error")}</p>;
  const game = hub.data.games[0];
  const running = hub.data.running;
  return (
    <div className="bt-duel-lobby">
      <div className="bt-duel-lobby__main">
        <header className="bt-page-header">
          <Link to="/games" className="bt-game-run__back">
            {t("games.back")}
          </Link>
          <h1 className="bt-page-title">{t("name", { ns })}</h1>
          <p className="bt-page-subtitle">{t("tagline", { ns })}</p>
        </header>

        <section className="bt-duel-lobby__section" aria-labelledby="duels-mine">
          <h2 id="duels-mine" className="bt-duel-lobby__section-title">
            {t("duels.lobby.mine")}
          </h2>
          {running.length === 0 ? (
            <p className="bt-duel-lobby__quiet">{t("duels.lobby.mine_empty")}</p>
          ) : (
            <ul className="bt-duel-list">
              {running.map((duel) => (
                <DuelRow key={duel.id} duel={duel} ns={ns} />
              ))}
            </ul>
          )}
        </section>

        <section className="bt-duel-lobby__section" aria-labelledby="duels-open">
          <h2 id="duels-open" className="bt-duel-lobby__section-title">
            {t("duels.lobby.open")}
          </h2>
          {hub.data.open.length === 0 ? (
            <p className="bt-duel-lobby__quiet">{t("duels.lobby.open_empty")}</p>
          ) : (
            <ul className="bt-duel-list">
              {hub.data.open.map((duel) => (
                <DuelRow key={duel.id} duel={duel} ns={ns} />
              ))}
            </ul>
          )}
        </section>

        <section className="bt-duel-lobby__section">
          {game ? <ChallengeForm game={game} ns={ns} /> : null}
        </section>

        {hub.data.recent.length > 0 ? (
          <section className="bt-duel-lobby__section" aria-labelledby="duels-recent">
            <h2 id="duels-recent" className="bt-duel-lobby__section-title">
              {t("duels.lobby.recent")}
            </h2>
            <ul className="bt-duel-list">
              {hub.data.recent.map((duel) => (
                <DuelRow key={duel.id} duel={duel} ns={ns} />
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <aside className="bt-duel-lobby__aside">
        <h2 className="bt-game-run__aside-title">{t("duels.rules")}</h2>
        <p className="bt-duel-lobby__rules">{t("rules", { ns })}</p>
        <Ratings gameKey={gameKey} />
      </aside>
    </div>
  );
}

/** One duel in a list: who, what pace, where it stands, and the one action it calls for. */
export function DuelRow({ duel, ns }: { duel: DuelSummary; ns: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const label = useTimeControlLabel();
  const reason = useReason();
  const action = useDuelAction();
  const opponent = opponentOf(duel);
  const href = `/duels/${duel.game_key}/${duel.id}`;
  const invited = duel.status === "invited";
  // An invitation addressed to this player, or an open one somebody else posted.
  const mineToAnswer = invited && !duel.is_challenger && duel.invitee !== null;
  const openToMe = invited && !duel.is_challenger && duel.invitee === null;
  const outcome = outcomeFor(duel);

  let state: string;
  if (invited) {
    if (openToMe) state = t("duels.state.open_by", { name: duel.challenger?.name ?? "" });
    else if (mineToAnswer)
      state = t("duels.state.challenges_you", { name: duel.challenger?.name ?? "" });
    else if (duel.invitee) state = t("duels.state.waiting_accept", { name: duel.invitee.name });
    else state = t("duels.state.open_mine");
  } else if (duel.status === "ready") state = t("duels.state.ready");
  else if (duel.status === "active")
    state = duel.your_turn
      ? t("duels.state.your_move")
      : duel.to_move
        ? t("duels.state.their_move", { name: opponent?.name ?? "" })
        : t("duels.state.playing");
  else if (outcome) state = `${t(`duels.outcome.${outcome}`)}, ${reason(ns, duel.result_reason)}`;
  else state = t(`duels.status.${duel.status}`);

  const change = duel.my_side ? duel.rating_change[duel.my_side] : undefined;
  const accept = () =>
    action.mutate({ id: duel.id, action: "accept" }, { onSuccess: () => navigate(href) });

  return (
    <li className="bt-duel-row" data-your-turn={duel.your_turn || mineToAnswer || undefined}>
      <Link to={href} className="bt-duel-row__who">
        <span className="bt-duel-row__name">
          {opponent ? t("duels.versus", { name: opponent.name }) : t("duels.no_opponent")}
        </span>
        <span className="bt-duel-row__meta">
          {label(duel.time_control)}
          {duel.is_rated ? "" : `, ${t("duels.unrated")}`}
        </span>
      </Link>
      <span className="bt-duel-row__state">{state}</span>
      {change !== undefined ? (
        <span className="bt-duel-row__change" data-sign={change >= 0 ? "up" : "down"}>
          {change >= 0 ? `+${change}` : `−${Math.abs(change)}`}
        </span>
      ) : null}
      {openToMe || mineToAnswer ? (
        <span className="bt-duel-row__actions">
          <Button size="sm" onClick={accept} loading={action.isPending}>
            {t("duels.accept")}
          </Button>
          {mineToAnswer ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => action.mutate({ id: duel.id, action: "decline" })}
            >
              {t("duels.decline")}
            </Button>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}

function Ratings({ gameKey }: { gameKey: string }) {
  const { t } = useTranslation();
  const [pace, setPace] = useState<Pace | null>(null);
  const board = useRatings(gameKey, pace);
  const data = board.data;
  return (
    <section className="bt-duel-ratings" aria-labelledby="duel-ratings">
      <h2 id="duel-ratings" className="bt-game-run__aside-title">
        {t("duels.ratings")}
      </h2>
      {data && data.paces.length > 1 ? (
        <div className="bt-duel-ratings__paces" role="group" aria-label={t("duels.pace_label")}>
          {data.paces.map((p) => (
            <button
              key={p}
              type="button"
              className="bt-duel-ratings__pace"
              aria-pressed={data.pace === p}
              onClick={() => setPace(p)}
            >
              {t(`duels.pace.${p}`)}
            </button>
          ))}
        </div>
      ) : null}
      {data && data.entries.length === 0 ? (
        <p className="bt-duel-lobby__quiet">{t("duels.ratings_empty")}</p>
      ) : null}
      {data && data.entries.length > 0 ? (
        <ol className="bt-duel-ratings__list">
          {data.entries.map((entry) => (
            <li
              key={entry.rank}
              className="bt-duel-ratings__row"
              data-me={entry.is_me || undefined}
            >
              <span className="bt-duel-ratings__rank">{entry.rank}</span>
              <span className="bt-duel-ratings__name">
                {entry.is_me ? t("games.you") : entry.participant}
              </span>
              <span className="bt-duel-ratings__rating">{entry.rating}</span>
              <span className="bt-duel-ratings__record">
                {t("duels.record", { wins: entry.wins, draws: entry.draws, losses: entry.losses })}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
